import React, { ReactNode, useState } from 'react'
import { Box, CircularProgress, Tooltip } from '@mui/material'

interface FancyProgressArcProps {
  // The metric value (0–100). Pass `null` / `undefined` to render the
  // neutral "N/A — not yet generated" state. A literal `0` is a real
  // result and renders as 0% with the bottom-band colour. Toggle
  // `zerona` to also collapse `0` into the N/A look (legacy
  // behaviour for callers that don't yet distinguish the two).
  value: number | null | undefined
  reverse?: boolean
  onecolor?: string
  zerona?: boolean
  tooltip?: string
  scale?: number
  icon?: ReactNode
  // While true the centre of the arc swaps from the percentage / N/A
  // label to a CircularProgress spinner — so callers can show that the
  // value is currently being recomputed without changing the arc's
  // size or hiding the surrounding track / icon. The previous value's
  // gradient stays visible so the user keeps the prior reading until
  // the new one lands.
  loading?: boolean
  // Optional override for the centre slot when the arc is in the N/A
  // state (no value yet). Lets callers drop a Generate-style button
  // (e.g. `<GenerateTraceButton question={question} />`) straight into
  // the middle of the empty arc, turning the arc itself into the
  // affordance to compute the missing value. The arc's `tooltip`
  // tooltip wrapper still surrounds it, so the parent Tooltip works
  // normally.
  naSlot?: ReactNode
}

type ColorBand = [minVal: number, startColor: string, stopColor: string]

const BANDS_REVERSE: ColorBand[] = [
  [80, '#DC143C', '#DA8D9D'],
  [60, '#DC5B74', '#D8A5AF'],
  [40, '#FF8C00', '#F1CFA6'],
  [20, '#68AC86', '#96B5A4'],
  [0, '#2E8B57', '#5A8C70'],
]

const BANDS_NORMAL: ColorBand[] = [
  [80, '#2E8B57', '#5A8C70'],
  [60, '#68AC86', '#96B5A4'],
  [40, '#FF8C00', '#F1CFA6'],
  [20, '#DC5B74', '#D8A5AF'],
  [0, '#DC143C', '#DA8D9D'],
]

function arcColors(value: number, reverse: boolean): [string, string] {
  const v = Number(value)
  const bands = reverse ? BANDS_REVERSE : BANDS_NORMAL
  for (const [min, start, stop] of bands) {
    if (v >= min) return [start, stop]
  }
  return ['#e7e7e8', '#e7e7e8']
}

const FancyProgressArc = React.forwardRef<HTMLDivElement, FancyProgressArcProps & React.HTMLAttributes<HTMLDivElement>>(function FancyProgressArc({
  value,
  reverse = false,
  onecolor,
  zerona = false,
  tooltip = '',
  scale = 1,
  icon,
  loading = false,
  naSlot,
  ...rest
}, ref) {
  const [gradId] = useState(() => 'fpag_' + Math.random().toString(36).slice(2, 9))

  // Null / undefined ⇒ "not yet generated" (renders N/A). When the
  // caller opts into `zerona` legacy mode, a literal `0` also collapses
  // into the N/A look. Otherwise `0` renders as a real `0%` with the
  // bottom-band colour.
  const showNA = value == null || (zerona && (value as number) <= 1)
  // Safe numeric for arc geometry / colour bands when not in the N/A
  // state — the SVG progress path is gated by `!showNA` so a 0 here
  // simply leaves the empty track visible.
  const numericValue: number = typeof value === 'number' ? value : 0
  const [startColor, stopColor] = onecolor ? [onecolor, onecolor] : arcColors(numericValue, reverse)

  const w = Math.round(46 * scale)
  const h = Math.round(41 * scale)
  const svgPx = Math.round(50 * scale)
  const dashArray = `${numericValue * 2}px, 400px`

  const arc = (
    <Box
      ref={ref}
      {...rest}
      sx={{
        width: w,
        height: h,
        mb: 0.1,
        position: 'relative',
        userSelect: 'none',
        flexShrink: 0,
      }}
    >
      {/* Percentage label */}
      <Box
        sx={{
          position: 'absolute',
          height: h,
          width: w,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          left: 1,
          top: 2,
          fontSize: numericValue > 99 ? Math.round(11 * scale) : Math.round(13 * scale),
          fontWeight: 600,
          lineHeight: 1,
          color: 'text.primary',
        }}
      >
        {loading ? (
          <CircularProgress size={Math.round(18 * scale)} thickness={5} sx={{ ml: -0.2 * scale }} />
        ) : showNA ? (
          naSlot ?? (
            <Box component="span" sx={{ fontSize: Math.round(10 * scale) }}>N/A</Box>
          )
        ) : (
          <Box component="span">
            {Math.round(numericValue)}
            <Box component="span" sx={{ fontSize: Math.round(10 * scale), fontWeight: 500 }}>%</Box>
          </Box>
        )}
      </Box>

      {/* SVG arc — `pointerEvents: none` so clicks pass through to
          the centre slot beneath (e.g. the `naSlot` Generate button).
          Without this the SVG, which is rendered after the label box
          and paints over it, catches every click on the arc's
          bounding rect. The outer Box's own `onClick` (when set in
          non-N/A states) still fires via bubble from the label box. */}
      <svg
        viewBox="0 0 110 110"
        style={{
          display: 'block',
          position: 'absolute',
          top: -4,
          left: -2,
          width: svgPx,
          height: svgPx,
          pointerEvents: 'none',
        }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="100%">
            <stop offset="0%" stopColor={startColor} />
            <stop offset="100%" stopColor={stopColor} />
          </linearGradient>
        </defs>

        <path
          d="M30,90 A40,40 0 1,1 80,90"
          fill="none"
          stroke="#e7e7e8"
          strokeWidth={18}
          strokeLinecap="round"
        />

        {!showNA && (
          <path
            d="M30,90 A40,40 0 1,1 80,90"
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth={18}
            strokeLinecap="round"
            strokeDasharray={dashArray}
            strokeDashoffset={0}
          />
        )}
      </svg>

      {/* Hide the underglow icon when the centre slot is rendering a
          caller-supplied `naSlot` (e.g. a Generate-Trace button) — the
          tiny mini-icon below would overlap visually with the button
          and read as duplicate signal. The icon stays visible for the
          ordinary value + N/A-text + loading states. */}
      {icon && !(showNA && naSlot) && (
        <Box sx={{ position: 'absolute', display: 'flex', alignItems: 'end', bottom: -3, border: 0, left: '50%', transform: 'translateX(-50%)', lineHeight: 2, height: 30 }}>
          {icon}
        </Box>
      )}
    </Box>
  )

  // Suppress the outer Tooltip when the centre is rendering a
  // caller-supplied `naSlot` — that slot brings its own tooltip
  // (e.g. GenerateTraceButton's "Generate Trace" / "Queued"), and
  // stacking both fires two popups for the same hover.
  const showNaSlot = showNA && !!naSlot
  if (tooltip && !showNaSlot) {
    return (
      <Tooltip title={tooltip} placement="left">
        {arc}
      </Tooltip>
    )
  }
  return arc
})

export default FancyProgressArc
